import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { format, isToday, isYesterday, parseISO } from 'date-fns'
import { CheckCircle2, Inbox } from 'lucide-react'
import { DetectionReview } from '@/components/alerts/DetectionReview'
import { AlertCard, ResolveAlertDialog } from '@/components/alerts'
import { PagePeaceState } from '@/components/common/PagePeaceState'
import { ParentSelector } from '@/components/family'
import { SectionHeader } from '@/components/hearth'
import {
  useFamily,
  useFamilyMembers,
  usePermissions,
  useAlerts,
  useAlert,
} from '@/hooks'
import { cn } from '@/lib/utils'
import type { Alert, AlertStatus, ResolutionType } from '@/types'

type FilterValue = 'needs_attention' | 'reviewed' | 'resolved' | 'all'

const FILTERS: { value: FilterValue; label: string; status?: AlertStatus }[] = [
  { value: 'needs_attention', label: 'Needs attention', status: 'UNREAD' },
  { value: 'reviewed', label: 'Reviewed', status: 'ACKNOWLEDGED' },
  { value: 'resolved', label: 'Resolved', status: 'RESOLVED' },
  { value: 'all', label: 'All' },
]

const EMPTY_STATES: Record<
  FilterValue,
  { title: string; description: string; icon?: typeof Inbox }
> = {
  needs_attention: {
    title: 'Nothing needs your attention',
    description:
      "No unread alerts. Check monitoring status and recent scan results above.",
  },
  reviewed: {
    title: 'Nothing in review',
    description: "Alerts you've acknowledged but not yet resolved will appear here.",
    icon: Inbox,
  },
  resolved: {
    title: 'No resolved alerts yet',
    description: "Alerts you've taken care of will appear here.",
    icon: CheckCircle2,
  },
  all: {
    title: "You're all caught up",
    description: "Alerts about your family's health and finances will appear here.",
  },
}

function dayLabel(dateString: string): string {
  const date = parseISO(dateString)
  if (isToday(date)) return 'Today'
  if (isYesterday(date)) return 'Yesterday'
  return format(date, 'EEEE, MMM d')
}

function AlertSkeleton({ stagger }: { stagger: number }) {
  return (
    <div
      className="hearth-card hearth-appear animate-pulse p-4"
      style={{ '--stagger': stagger } as React.CSSProperties}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 shrink-0 rounded-full bg-gray-200" />
        <div className="flex-1">
          <div className="h-4 w-3/4 rounded bg-gray-200" />
          <div className="mt-2 h-3 w-full rounded bg-gray-200" />
          <div className="mt-3 h-3 w-1/4 rounded bg-gray-200" />
        </div>
      </div>
    </div>
  )
}

export function AlertsPage() {
  const [searchParams] = useSearchParams()
  const { alertId } = useParams()
  const highlightedAlertId = searchParams.get('id') ?? alertId ?? null

  const [page, setPage] = useState(1)
  const [actionError, setActionError] = useState<string | null>(null)
  const [activeFilter, setActiveFilter] = useState<FilterValue>('needs_attention')
  const [expandedAlertId, setExpandedAlertId] = useState<string | null>(
    highlightedAlertId
  )
  const [resolveTarget, setResolveTarget] = useState<Alert | null>(null)

  const { selectedFamilyId } = useFamily()
  const { selectedMemberId } = useFamilyMembers(selectedFamilyId)
  const { isCaregiver, isParent } = usePermissions()

  const statusFilter = FILTERS.find((f) => f.value === activeFilter)?.status

  const {
    alerts,
    error,
    refetch,
    pagination,
    isLoading,
    acknowledgeAlert,
    resolveAlert,
    isAcknowledging,
    isResolving,
  } = useAlerts(selectedFamilyId, {
    status: statusFilter,
    member_id: isCaregiver ? selectedMemberId ?? undefined : undefined,
    page_size: 20,
    page,
  })

  const linkedAlert = useAlert(selectedFamilyId, highlightedAlertId)
  const displayedAlerts = useMemo(() => linkedAlert.data && !alerts.some((alert) => alert.id === linkedAlert.data.id)
    ? [linkedAlert.data, ...alerts]
    : alerts, [linkedAlert.data, alerts])

  useEffect(() => {
    setPage(1)
    setResolveTarget(null)
    setActionError(null)
  }, [selectedFamilyId, selectedMemberId])

  useEffect(() => {
    setExpandedAlertId(highlightedAlertId)
  }, [highlightedAlertId])

  // Bring the deep-linked (?id=) alert into view once it's loaded.
  useEffect(() => {
    if (!highlightedAlertId || isLoading) return
    const element = document.getElementById(`alert-${highlightedAlertId}`)
    element?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [highlightedAlertId, isLoading, linkedAlert.data])

  // Group alerts by calendar day, preserving API order.
  const dayGroups = useMemo(() => {
    const groups: { label: string; alerts: Alert[] }[] = []
    for (const alert of displayedAlerts) {
      const label = dayLabel(alert.created_at)
      const last = groups[groups.length - 1]
      if (last && last.label === label) {
        last.alerts.push(alert)
      } else {
        groups.push({ label, alerts: [alert] })
      }
    }
    return groups
  }, [displayedAlerts])

  const handleAcknowledge = async (alertId: string) => {
    setActionError(null)
    try {
      await acknowledgeAlert({ alertId })
    } catch {
      setActionError('Could not acknowledge this alert. Please try again.')
    }
  }

  const handleResolve = async (resolution: ResolutionType) => {
    if (!resolveTarget) return
    setActionError(null)
    try {
      await resolveAlert({
        alertId: resolveTarget.id,
        data: { resolution },
      })
      setResolveTarget(null)
    } catch {
      setActionError('Could not resolve this alert. Please try again.')
    }
  }

  const emptyState = EMPTY_STATES[activeFilter]
  let staggerIndex = 0

  if (!isCaregiver) {
    return <PagePeaceState title="Alerts are for caregivers" description="Only active caregivers can view and manage family alerts." />
  }

  return (
    <div className="space-y-6">
      {actionError && <p role="alert" className="text-red-700">{actionError}</p>}
      {linkedAlert.error && <p role="alert">This linked alert could not be loaded. It may belong to a family you cannot access.</p>}
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-gray-900">Alerts</h1>
          <p className="mt-1 text-sm text-gray-500">
            What we've noticed about your family's health and finances
          </p>
        </div>
        {isCaregiver && !isParent && <ParentSelector />}
      </div>

      {selectedFamilyId && <DetectionReview key={selectedFamilyId} familyId={selectedFamilyId} />}

      {/* Segmented filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full bg-gray-100 p-1">
          {FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => { setActiveFilter(filter.value); setPage(1) }}
              className={cn(
                'rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors',
                activeFilter === filter.value
                  ? 'bg-surface text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              {filter.label}
            </button>
          ))}
        </div>
        {pagination && !isLoading && (
          <span className="font-rounded text-xs tabular-nums text-gray-400">
            {pagination.total_count} {pagination.total_count === 1 ? 'alert' : 'alerts'}
          </span>
        )}
      </div>

      {/* Alerts, grouped by day */}
      {error ? (
        <div role="alert">Alerts could not be loaded. <button onClick={() => refetch()}>Try again</button></div>
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <AlertSkeleton key={i} stagger={i} />
          ))}
        </div>
      ) : displayedAlerts.length > 0 ? (
        <div className="space-y-6">
          {dayGroups.map((group) => (
            <section key={group.label} className="space-y-3">
              <SectionHeader title={group.label} />
              {group.alerts.map((alert) => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  expanded={expandedAlertId === alert.id}
                  highlighted={highlightedAlertId === alert.id}
                  stagger={staggerIndex++}
                  onToggle={() =>
                    setExpandedAlertId((current) =>
                      current === alert.id ? null : alert.id
                    )
                  }
                  onAcknowledge={() => handleAcknowledge(alert.id)}
                  onResolveRequest={() => setResolveTarget(alert)}
                  isAcknowledging={isAcknowledging}
                  isResolving={isResolving}
                />
              ))}
            </section>
          ))}
        </div>
      ) : (
        <PagePeaceState
          icon={emptyState.icon}
          title={emptyState.title}
          description={emptyState.description}
          variant={activeFilter === 'reviewed' || activeFilter === 'resolved' ? 'primary' : 'success'}
        />
      )}

      {pagination && (pagination.total_pages > 1 || page > 1) && (
        <div className="flex items-center justify-between">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span>Page {page} of {pagination.total_pages}</span>
          <button disabled={page >= pagination.total_pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
      <ResolveAlertDialog
        open={resolveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setResolveTarget(null)
        }}
        alertTitle={resolveTarget?.title}
        onResolve={handleResolve}
        isResolving={isResolving}
        error={actionError}
      />
    </div>
  )
}
